import { AfterViewInit, ChangeDetectorRef, Directive, Input, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { ColumnDefinition } from '../../model/column-definition';
import { CommandBase, DeleteCommand, FilteredCommand, standardCommand } from '../../model/command';
import { DynamicExcelExportProvider } from '../../model/dynamic-excel-export-provider';
import { AdditionalExcelSheetItem } from '../../model/selectable-items';
import { FilterDefinitionItem } from '../../model/filter-definition-item';
import ListViewColumn from '../../model/list-view-column';
import { MenuItemDefinition } from '../../model/menu-item-definition';
import { ModelBase } from '../../model/model-base';
import { DataService, IModel } from '../../service/base/data.service';
import { CommandDispatcher } from '../../service/command/command-dispatcher.service';
import { CommandHandlerResolver } from '../../service/command/command-handler-resolver.service';
import { AllowSort } from '../../service/factory/allow-sort';
import { NavigationService } from '../../service/navigation.service';
import { CommandController } from '../base/command-controller.base';
import { FilterComponent } from '../list/filter/filter.component';
import { SortEvent, State } from '../list/list-view-header/list-view-header.interface';
import { DefaultPageSize, PagingEvent } from '../list/paging/paging.component';
import { MessageService } from './../../../core/service/message.service';
import { SidebarService } from '../../service/sidebar.service';
import { UserPreferencesService } from '../../service/user-preferences.service';
import { getListPreferencesId } from '../../helper/list-preferences-key';

export interface PagedSidebarMenuAction<TCommand extends CommandBase = CommandBase> {
  commandId: string;
  template: TemplateRef<any>;
  createDataContext?: (command: TCommand) => any;
  onClose?: () => void;
}

@Directive()
export abstract class PagedObjectManagerComponent<
  TModel extends ModelBase,
  TService extends DataService<TModel, any, IModel> = DataService<TModel>,
  TModelCreate extends TService extends DataService<TModel, infer U, IModel> ? U : any = TService extends DataService<
    TModel,
    infer U,
    IModel
  >
    ? U
    : TModel,
  TModelUpdate extends TService extends DataService<TModel, TModelCreate, infer U> ? U : IModel = TService extends DataService<
    TModel,
    TModelCreate,
    infer U
  >
    ? U
    : TModel,
>
  extends CommandController
  implements AfterViewInit, OnInit, AllowSort
{
  constructor(
    protected readonly dataService: TService,
    protected readonly messageService: MessageService,
    protected readonly navigation: NavigationService,
    commandDispatcher: CommandDispatcher,
    handlerResolver: CommandHandlerResolver,
    private readonly pagedDataService: DataService<TModel> = dataService,
    protected sidebarService?: SidebarService
  ) {
    super(commandDispatcher, handlerResolver);
  }

  @ViewChild(FilterComponent) filterComponent: FilterComponent;
  preferencesKey: string;
  cdr = inject(ChangeDetectorRef);
  private readonly userPreferences = inject(UserPreferencesService);
  private preferencesId?: number;
  private preferencesReady = false;
  private paginationInitialized = false;
  private pendingFilterState: any;
  allowNew: boolean = true;
  allowOpenOnRowClick: boolean = true;
  columnDefinition: ColumnDefinition[];
  excelColumnDefinition: ColumnDefinition[] = [];
  excelAdditionalSheetItems: AdditionalExcelSheetItem[] = [];
  contextMenuDefinition: MenuItemDefinition[] = [];
  menuDefinition: MenuItemDefinition[] = [];
  filterDefinition: FilterDefinitionItem[];
  defaultFilters: string[];
  itemsSource: TModel[];
  title: string;
  helpText: string;
  totalResults = 0;
  pageNumber = 1;
  pageSize = DefaultPageSize;
  showExcelExportButton: boolean = false;
  excelFileName: string;
  excelContext: string;
  columns: ListViewColumn[];
  selectedIds: string[] = [];
  deSelectedIds: string[] = [];
  affectedItemsCount = 0;
  allSelected: boolean;
  sidebarMenuActionCommandIds: string[] = [];
  sidebarMenuDataContext: any;
  private sidebarMenuActions: PagedSidebarMenuAction[] = [];

  allowSort(column: ListViewColumn): boolean {
    return this.totalResults <= this.pageSize;
  }

  protected addMenuItemDefinition(caption: string, commandName: string): void {
    if (!this.menuDefinition.find(item => item.commandId == commandName))
      this.menuDefinition.push(new MenuItemDefinition(caption, commandName));
  }

  protected addContextMenuItemDefinition(caption: string, commandName: string, disableItem?: (context: any) => boolean): void {
    if (!this.contextMenuDefinition.find(item => item.commandId == commandName))
      this.contextMenuDefinition.push(new MenuItemDefinition(caption, commandName, null, disableItem));
  }

  protected addEditDeleteCommands(disableDelete?: (context: any) => boolean) {
    if (this.allowOpenOnRowClick) {
      this.addContextMenuItemDefinition('EDIT_BTN', standardCommand.edit);
    }
    this.addContextMenuItemDefinition('DELETE_BTN', standardCommand.delete, disableDelete);
  }

  get selectedColumns(): ListViewColumn[] {
    return this.columns.filter(x => x.selected);
  }

  get getSelectedIds(): string[] {
    return this.selectedIds;
  }

  get getDeselectedIds(): string[] {
    return this.deSelectedIds;
  }

  get getAffectedItemsCount(): number {
    return this.affectedItemsCount;
  }

  get getAllSelected(): boolean {
    return this.allSelected;
  }

  get excelExportProvider(): DynamicExcelExportProvider | null {
    const provider = this.pagedDataService as unknown as DynamicExcelExportProvider;
    return provider?.getDynamicExcelExport ? provider : null;
  }

  private _coerceCommand(command: CommandBase) {
    if (command instanceof DeleteCommand) {
      command.dataService = this.dataService;
      command.itemsSource = this.itemsSource;
    } else if (command instanceof FilteredCommand) {
      command.dataService = this.pagedDataService;
      command.filterProvider = this;
    }
  }

  canExecute(command: CommandBase): boolean {
    this._coerceCommand(command);
    return super.canExecute(command);
  }

  execute(command: CommandBase) {
    this._coerceCommand(command);
    super.execute(command);
  }

  registerSidebarMenuAction(action: PagedSidebarMenuAction): void {
    if (!action?.commandId || !action.template) return;

    this.sidebarMenuActions = this.sidebarMenuActions.filter(item => item.commandId !== action.commandId);
    this.sidebarMenuActions.push(action);

    if (!this.sidebarMenuActionCommandIds.includes(action.commandId)) {
      this.sidebarMenuActionCommandIds = [...this.sidebarMenuActionCommandIds, action.commandId];
    }
  }

  onMenuCommand(command: CommandBase): void {
    this._coerceCommand(command);
    const action = this.sidebarMenuActions.find(item => item.commandId === command.id);
    if (!action) {
      this.execute(command);
      return;
    }

    if (!this.sidebarService) {
      this.messageService.showError('Sidebar service is not available.');
      return;
    }

    this.sidebarMenuDataContext = action.createDataContext ? action.createDataContext(command) : command;
    this.sidebarService.setSidebarContent({
      template: action.template,
      onClose: () => {
        action.onClose?.();
        this.sidebarMenuDataContext = null;
      },
    });
    this.sidebarService.toggleSidebar(true);
  }

  closeSidebarMenuAction(): void {
    this.sidebarService?.toggleSidebar(false);
  }

  protected executedWithResult(result: boolean, command: CommandBase) {
    if (result) {
      this.sendPageFilter(this.filterState);
    }
  }

  ngOnInit() {
    this.addEditDeleteCommands();
    this.initializePagination();
  }

  private initializePagination(): void {
    if (this.paginationInitialized) return;
    if (!this.preferencesKey) {
      this.preferencesReady = true;
      return;
    }
    this.paginationInitialized = true;
    this.preferencesReady = false;
    const requestedPage = Number(this.navigation.getParameterValue('page'));
    if (Number.isSafeInteger(requestedPage) && requestedPage > 1 && requestedPage <= 1000000) {
      this.pageNumber = requestedPage;
    }

    this.preferencesId = getListPreferencesId(this.preferencesKey, this.navigation.url);
    this.AddSubscription(
      this.userPreferences.getListPreferences(this.preferencesId).subscribe({
        next: preferences => {
          if (Number.isInteger(preferences.pageSize) && preferences.pageSize > 0) {
            this.pageSize = preferences.pageSize;
          }
          this.finishPreferencesLoad();
        },
        error: () => this.finishPreferencesLoad(),
      })
    );
  }

  private finishPreferencesLoad(): void {
    this.preferencesReady = true;
    if (this.pendingFilterState !== undefined) {
      const filter = this.pendingFilterState;
      this.pendingFilterState = undefined;
      this.onFilterChange(filter);
    }
  }

  ngAfterViewInit() {
    window.scrollTo(0, 0);
  }

  get excelReady(): boolean {
    return this.columns?.length > 0 === true;
  }

  onColumnChange(columns: ListViewColumn<TModel>[]) {
    this.columns = columns;
    this.cdr.detectChanges();
  }

  onSelectedIdsChange(selectedIds: string[]) {
    this.selectedIds = selectedIds;
    this.cdr.detectChanges();
  }

  onDeselectedIdsChange(deSelectedIds: string[]) {
    this.deSelectedIds = deSelectedIds;
    this.cdr.detectChanges();
  }
  onAffectedItemsCountChange(affectedItemsCount: number) {
    this.affectedItemsCount = affectedItemsCount;
    this.cdr.detectChanges();
  }
  onAllSelectedChange(allSelected: boolean) {
    this.allSelected = allSelected;
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (this.filterSubscription) this.filterSubscription.unsubscribe();
  }

  onCreateItem() {
    this.navigation.navigateToForm();
  }

  private filterSubscription: Subscription;

  onFilterChange(filterState: any) {
    this.initializePagination();
    if (!filterState.selectedFields || this.filterDefinition === undefined) return;
    if (!this.preferencesReady) {
      this.pendingFilterState = filterState;
      return;
    }
    // Filter controls can emit while a restored page loads. Keep that page
    // until the response shows it is outside the available range.
    this.filterState = filterState;
    this.filterInternal(this.filterState);
    this.sendPageFilter(filterState);
  }

  filterInternal(filterState: any) {
    return;
  }

  private sortOrder: string;
  private sortState: State;

  sortChanged(event: SortEvent) {
    if (this.sortOrder === event.column.serverSideSortBinding && this.sortState === event.state) {
      return;
    }
    this.sortOrder = event.state === State.DEFAULT ? null : event.column.serverSideSortBinding;
    this.sortState = event.state;
    if (this.totalResults > this.pageSize) {
      this.sendPageFilter(this.filterState ?? {});
    }
  }

  filterState: any;

  protected preparePageFilter(filter: any): any {
    return filter;
  }

  private sendPageFilter(filter: any) {
    if (this.filterSubscription) {
      this.filterSubscription.unsubscribe();
    }

    this.filterSubscription = this.pagedDataService
      .getPagedList(this.preparePageFilter(filter), this.pageNumber, this.pageSize, this.sortOrder, this.sortState === State.ORDERBYDESC)
      .subscribe({
        next: result => {
          const lastPage = Math.max(1, Math.ceil(result.totalResults / this.pageSize));
          if (this.pageNumber > lastPage) {
            this.pageNumber = lastPage;
            if (this.preferencesKey) this.navigation.setListPage(lastPage);
            this.sendPageFilter(filter);
            return;
          }
          this.itemsSource = result.items;
          this.totalResults = result.totalResults;
          if (typeof result.page === 'number' && result.page >= 1) {
            this.pageNumber = result.page;
          }
        },
        error: error => this.messageService.showError(error),
      });
  }

  getPagingChangedFallbackFilter(): any {
    return {};
  }

  pagingChanged(e: PagingEvent) {
    this.initializePagination();
    if (this.pageNumber === e.pageNumber && this.pageSize === e.itemsPerPage) {
      return;
    }
    this.filterSubscription?.unsubscribe();
    const pageSizeChanged = this.pageSize !== e.itemsPerPage;
    if (typeof e.pageNumber === 'number' && e.pageNumber >= 1) {
      this.pageNumber = e.pageNumber;
    }
    if (typeof e.itemsPerPage === 'number' && e.itemsPerPage >= 1) {
      this.pageSize = e.itemsPerPage;
    }
    if (this.preferencesKey) this.navigation.setListPage(this.pageNumber);
    const loadPage = () => {
      if (!this.filterComponent) {
        this.sendPageFilter(this.getPagingChangedFallbackFilter());
        return;
      }

      const filterValues = this.filterComponent.form.value;
      filterValues.selectedFields = this.filterComponent.filters
        .filter((filter: FilterDefinitionItem) => filter.selected)
        .map((filter: FilterDefinitionItem) => filter.name);

      this.filterInternal(filterValues);
      this.sendPageFilter(filterValues);
    };

    if (pageSizeChanged && this.preferencesId !== undefined) {
      this.AddSubscription(this.userPreferences.setListPageSize(this.preferencesId, this.pageSize).subscribe({
        next: loadPage,
        error: error => {
          this.messageService.showError(error);
          loadPage();
        },
      }));
      return;
    }

    loadPage();
  }

  onRowClicked(e: { context: TModel; event: PointerEvent }) {
    if (this.allowOpenOnRowClick) {
      this.navigation.navigateToForm(e.context.id, e.event.ctrlKey);
    }
  }

  addListMenuItem(caption: string, commandName: string): void {
    if (!this.menuDefinition.find(item => item.commandId == commandName))
      this.menuDefinition.push(new MenuItemDefinition(caption, commandName, null, null, this.contextId));
  }
}
